import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router";
import { selectIsAuthenticated, selectUser } from "@/features/auth/store/sessionSlice";
import { PRONOSTICS_QUERY_KEYS } from "@/features/pronostics/configs/queryKeys";
import type { PronosticCatalogCompetition } from "@/features/pronostics/types/pronostic.types";
import { checkRouteReadPermission, findRouteByPath } from "@/shared/configs/routeRegistry.config";
import { useRepresentativePermissions } from "@/shared/hooks/useRepresentativePermissions";
import { FeatureTeasingService as api } from "@/shared/services/featureTeasing.service";
import type { CampaignAnswer, CampaignOpening } from "@/shared/types/featureTeasing.types";
import { CampaignTeaserModal } from "./CampaignTeaserModal";
import { selfServicePath } from "./campaignTeaser.config";
import { CampaignVisitContext, type CampaignVisitState } from "./campaignVisitContext";

const VisitProvider = ({ children, organizationId }: { children: ReactNode, organizationId: string }) => {
  const client = useQueryClient();
  const navigate = useNavigate();
  const goTo = useCallback((path: string) => {
    void navigate(path);
  }, [navigate]);
  const { permissions } = useRepresentativePermissions();
  const [opening, setOpening] = useState<CampaignOpening | null>(null);
  const [returnFocus, setReturnFocus] = useState<HTMLElement | null>(null);
  const [visit] = useState<CampaignVisitState>(() => {
    let occupied = false;
    let pending = false;
    return {
      consumed: false,
      beginManual() {
        if (occupied || pending) return false;
        this.consumed = true;
        pending = true;
        return true;
      },
      endManual() { pending = false; },
      present(result, target) {
        if (occupied) return;
        const offer = result.campaign.offer;
        const competition = offer?.resource_kind === "competition"
          ? client.getQueryData<PronosticCatalogCompetition[]>(PRONOSTICS_QUERY_KEYS.catalog())?.find((item) => item.id === offer.resource_id)
          : undefined;
        if (competition?.purchase_requested || competition?.subscribed) {
          pending = false;
          this.consumed = true;
          this.reservation = undefined;
          return;
        }
        occupied = true;
        pending = false;
        this.consumed = true;
        setReturnFocus(target ?? null);
        setOpening(result);
      },
      close() {
        occupied = false;
        setOpening(null);
      },
    };
  });
  const invalidate = useCallback(() => {
    void client.invalidateQueries({ queryKey: ["current-offers", organizationId] });
  }, [client, organizationId]);
  const invalidateCatalog = useCallback(() => {
    void client.invalidateQueries({ queryKey: PRONOSTICS_QUERY_KEYS.catalog() });
  }, [client]);
  // Self-serve is only offered when the target route is readable by this elu, otherwise
  // the answer would be recorded (terminal) and the elu would land on AccessDeniedPage.
  const selfServeTarget = selfServicePath(opening?.campaign.offer?.feature_code);
  const targetRoute = selfServeTarget ? findRouteByPath(selfServeTarget) : undefined;
  const canSelfServe = !!selfServeTarget && (!targetRoute || checkRouteReadPermission(targetRoute, permissions));
  const campaignId = opening?.campaign.id ?? "";
  const shown = useCallback(async (id: string) => {
    try {
      await api.shown(organizationId, campaignId, id);
    }
    catch (error) {
      invalidateCatalog();
      invalidate();
      throw error;
    }
  }, [organizationId, campaignId, invalidate, invalidateCatalog]);
  const respond = useCallback(async (id: string, answer: CampaignAnswer) => {
    try {
      const result = await api.respond(organizationId, campaignId, id, answer);
      if (result.response === "interested") {
        invalidateCatalog();
      }
      return result;
    }
    catch (error) {
      invalidateCatalog();
      throw error;
    }
    finally { invalidate(); }
  }, [organizationId, campaignId, invalidate, invalidateCatalog]);
  const refresh = useCallback(async (id: string) => {
    try {
      return await api.refresh(organizationId, campaignId, id);
    }
    catch (error) {
      invalidateCatalog();
      invalidate();
      throw error;
    }
  }, [organizationId, campaignId, invalidate, invalidateCatalog]);
  return (
    <CampaignVisitContext.Provider value={visit}>
      {children}
      {opening && (
        <CampaignTeaserModal
          key={opening.opening_id}
          opening={opening}
          returnFocus={returnFocus}
          onShown={shown}
          onRespond={respond}
          onRefresh={refresh}
          onNavigate={canSelfServe ? goTo : undefined}
          onClose={visit.close}
        />
      )}
    </CampaignVisitContext.Provider>
  );
};

export const CampaignVisitBoundary = ({ children = <Outlet /> }: { children?: ReactNode }) => {
  const authenticated = useSelector(selectIsAuthenticated);
  const representative = useSelector(selectUser)?.representative;
  const identity = JSON.stringify([representative?.organization_id, representative?.id]);
  if (!authenticated || !representative?.organization_id || !representative.id) return children;
  return <VisitProvider key={identity} organizationId={representative.organization_id}>{children}</VisitProvider>;
};
